import { useState } from "react"
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  EmailShareButton,
  FacebookIcon,
  WhatsappIcon,
  LinkedinIcon,
  EmailIcon,
  XIcon,
} from "react-share"
import { X, Copy, Check } from "lucide-react"
import { useClipboard } from "use-clipboard-copy"
import { toast } from "sonner"
import { Text } from "@/components/Primitives/Text"
import { Button } from "@/components/Primitives/Button"
import { useConfig } from "@/hooks/useConfig"

interface ShareModalProps {
  isOpen: boolean
  onClose: () => void
  url: string
  title: string
  text?: string
}

export function ShareModal({
  isOpen,
  onClose,
  url,
  title,
  text,
}: ShareModalProps) {
  const [isCopied, setIsCopied] = useState(false)
  const clipboard = useClipboard()
  const { t } = useConfig()

  const handleCopyLink = () => {
    clipboard.copy(url)
    setIsCopied(true)
    toast.success(t("config_share_link_copied"), {
      style: {
        backgroundColor: "#4ade80",
        color: "white",
        border: "1px solid #4ade80",
      },
    })

    setTimeout(() => {
      setIsCopied(false)
    }, 2000)
  }

  if (!isOpen) return null

  const iconSize = 40
  const shareMessage = text || title

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mb-6">
          <Text variant="lead" className="text-[1.25rem]" bold>
            {t("config_share_mission_title")}
          </Text>
          <Text variant="small" className="text-gray-500">
            {t("config_share_choose_method")}
          </Text>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Text bold>{t("config_share_copy_link")}</Text>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-100 rounded-lg p-2 text-sm text-gray-600 truncate">
              {url}
            </div>
            <Button
              onClick={handleCopyLink}
              variant="outline"
              className="flex items-center gap-2"
            >
              {isCopied ? (
                <>
                  <Check className="h-4 w-4" />
                  <span>{t("config_share_copied")}</span>
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  <span>{t("config_share_copy")}</span>
                </>
              )}
            </Button>
          </div>
        </div>

        <div>
          <div className="mb-3">
            <Text bold>{t("config_share_social_title")}</Text>
          </div>
          <div className="flex flex-row flex-nowrap justify-center gap-6 overflow-x-auto">
            <div className="flex flex-col items-center w-20 gap-1">
              <FacebookShareButton url={url} hashtag="#PandorasWay">
                <FacebookIcon size={iconSize} round />
              </FacebookShareButton>
              <Text variant="small" className="block w-full text-center mt-1">
                Facebook
              </Text>
            </div>
            <div className="flex flex-col items-center w-20 gap-1">
              <TwitterShareButton url={url} title={shareMessage}>
                <XIcon size={iconSize} round />
              </TwitterShareButton>
              <Text variant="small" className="block w-full text-center mt-1">
                X
              </Text>
            </div>
            <div className="flex flex-col items-center w-20 gap-1">
              <WhatsappShareButton
                url={url}
                title={shareMessage}
                separator=":: "
              >
                <WhatsappIcon size={iconSize} round />
              </WhatsappShareButton>
              <Text variant="small" className="block w-full text-center mt-1">
                WhatsApp
              </Text>
            </div>
            <div className="flex flex-col items-center w-20 gap-1">
              <LinkedinShareButton url={url} title={shareMessage}>
                <LinkedinIcon size={iconSize} round />
              </LinkedinShareButton>
              <Text variant="small" className="block w-full text-center mt-1">
                LinkedIn
              </Text>
            </div>
            <div className="flex flex-col items-center w-20 gap-1">
              <EmailShareButton url={url} subject={title} body={text}>
                <EmailIcon size={iconSize} round />
              </EmailShareButton>
              <Text variant="small" className="block w-full text-center mt-1">
                Email
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
